/**
 * Berechnet die Anrufzeit-Angabe für die Bestätigungsseite (AfterSave).
 *
 * Grundsatz: es wird durchgehend mit der *Wanduhrzeit* in cfg.timezone gerechnet
 * (Europe/Berlin), nie mit der Gerätezeitzone. Wir rechnen dabei nie von einer
 * Wanduhrzeit zurück auf einen Zeitstempel — dadurch spielen Sommer-/Winterzeit
 * und DST-Sprünge von vornherein keine Rolle.
 *
 * Jeder Fehler endet in FALLBACK_TEXT ("in Kürze"). Es darf nie ein leerer
 * String, "Invalid Date" oder ein sichtbarer Platzhalter herauskommen.
 */

export const FALLBACK_TEXT = 'in Kürze';

const WEEKDAYS_DE = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];

/** Wie viele Tage maximal nach der nächsten Öffnung gesucht wird (Endlosschleifen-Schutz). */
const MAX_LOOKAHEAD_DAYS = 14;

/**
 * Wanduhrzeit in der Zielzeitzone als reine Kalenderangabe.
 * @returns {{year:number, month:number, day:number, hour:number, minute:number, isoWeekday:number}}
 */
function civilInTimeZone(date, timeZone) {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).formatToParts(date);

    const get = (type) => {
        const p = parts.find((x) => x.type === type);
        return p ? parseInt(p.value, 10) : NaN;
    };

    const year = get('year');
    const month = get('month');
    const day = get('day');
    // Manche Engines liefern bei Mitternacht "24" statt "00".
    const hour = get('hour') % 24;
    const minute = get('minute');

    if ([year, month, day, hour, minute].some((n) => Number.isNaN(n))) {
        throw new Error('Zeitzonen-Umrechnung fehlgeschlagen für ' + timeZone);
    }

    return { year, month, day, hour, minute, isoWeekday: isoWeekdayOf(year, month, day) };
}

/** ISO-Wochentag (1 = Mo … 7 = So) aus einer reinen Kalenderangabe. */
function isoWeekdayOf(year, month, day) {
    const dow = new Date(Date.UTC(year, month - 1, day)).getUTCDay(); // 0 = So
    return dow === 0 ? 7 : dow;
}

/** Kalendertag + n Tage, rein kalendarisch (kein Zeitzonenbezug). */
function addDays({ year, month, day }, n) {
    const d = new Date(Date.UTC(year, month - 1, day + n));
    const y = d.getUTCFullYear();
    const m = d.getUTCMonth() + 1;
    const dd = d.getUTCDate();
    return { year: y, month: m, day: dd, isoWeekday: isoWeekdayOf(y, m, dd) };
}

/** "2026-10-03" */
function dateKey({ year, month, day }) {
    return [
        String(year).padStart(4, '0'),
        String(month).padStart(2, '0'),
        String(day).padStart(2, '0'),
    ].join('-');
}

/** "08:00" -> Minuten seit Mitternacht. Wirft bei unbrauchbarem Wert. */
function parseHHMM(value) {
    const m = /^(\d{1,2}):(\d{2})$/.exec(String(value).trim());
    if (!m) throw new Error('Unbrauchbare Uhrzeit in business_hours: ' + value);
    const h = parseInt(m[1], 10);
    const min = parseInt(m[2], 10);
    if (h > 23 || min > 59) throw new Error('Uhrzeit außerhalb des gültigen Bereichs: ' + value);
    return h * 60 + min;
}

/** Minuten seit Mitternacht -> "8:00" (Stunde ohne führende Null, wie im Text gewünscht). */
function formatHHMM(minutes) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return h + ':' + String(m).padStart(2, '0');
}

/**
 * Query-Parameter dieses Konfigurators überschreiben Config-Werte per
 * Dot-Notation (?steps.4.call_eta.enabled=false) — dabei kommt immer ein String
 * an. Damit QA die Anrufzeit ohne Deploy testen kann, werden Strings hier
 * tolerant ausgewertet.
 */
function asBool(value) {
    if (value === true || value === false) return value;
    if (typeof value === 'string') {
        const v = value.trim().toLowerCase();
        if (v === 'true' || v === '1') return true;
        if (v === 'false' || v === '0') return false;
    }
    return null;
}

function asNumber(value) {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (typeof value === 'string' && value.trim() !== '') {
        const n = Number(value);
        return Number.isFinite(n) ? n : null;
    }
    return null;
}

function isHoliday(civilDate, cfg) {
    if (!Array.isArray(cfg.holidays)) return false;
    return cfg.holidays.indexOf(dateKey(civilDate)) > -1;
}

/** Öffnungszeiten-Fenster eines Kalendertags, oder null wenn geschlossen. */
function openingWindow(civilDate, cfg) {
    if (isHoliday(civilDate, cfg)) return null;
    const hours = cfg.business_hours && cfg.business_hours[civilDate.isoWeekday];
    if (!Array.isArray(hours) || hours.length < 2) return null;
    const open = parseHHMM(hours[0]);
    const close = parseHHMM(hours[1]);
    if (close <= open) throw new Error('business_hours: Schließzeit liegt vor Öffnungszeit');
    return { open, close };
}

/**
 * Nächste Öffnung ab jetzt. Überspringt Wochenenden und Feiertage.
 * @returns {{civilDate:object, open:number, dayOffset:number}|null}
 */
function nextBusinessOpening(civilNow, cfg) {
    const nowMinutes = civilNow.hour * 60 + civilNow.minute;

    for (let offset = 0; offset <= MAX_LOOKAHEAD_DAYS; offset++) {
        const candidate = offset === 0 ? civilNow : addDays(civilNow, offset);
        const window = openingWindow(candidate, cfg);
        if (!window) continue;
        // Heute nur dann, wenn die Öffnung noch bevorsteht.
        if (offset === 0 && nowMinutes >= window.open) continue;
        return { civilDate: candidate, open: window.open, dayOffset: offset };
    }
    return null;
}

/**
 * Kern der Aufgabe: Text für {{ANRUFZEIT}}.
 *
 * @param {Date|number} now  Zeitpunkt (bevorzugt Serverzeit, siehe ServerTime.js)
 * @param {object} cfg       call_eta-Konfiguration aus dem AfterSave-Step
 * @returns {string}         z.B. "in ca. 2 Minuten", "morgen ab 8:00 Uhr", "in Kürze"
 */
export function callEtaText(now, cfg) {
    try {
        if (!cfg || asBool(cfg.enabled) !== true) return FALLBACK_TEXT;

        const date = now instanceof Date ? now : new Date(now);
        if (Number.isNaN(date.getTime())) return FALLBACK_TEXT;

        const timezone = cfg.timezone || 'Europe/Berlin';
        const civilNow = civilInTimeZone(date, timezone);

        const todayWindow = openingWindow(civilNow, cfg);
        if (todayWindow) {
            const nowMinutes = civilNow.hour * 60 + civilNow.minute;
            const inBusinessTime = nowMinutes >= todayWindow.open && nowMinutes < todayWindow.close;
            const minutesUntilClose = todayWindow.close - nowMinutes;
            const cutoffValue = asNumber(cfg.cutoff_minutes);
            const cutoff = cutoffValue === null ? 0 : cutoffValue;

            if (inBusinessTime && minutesUntilClose > cutoff) {
                const eta = asNumber(cfg.eta_minutes);
                if (eta === null || eta <= 0) return FALLBACK_TEXT;
                return 'in ca. ' + eta + (eta === 1 ? ' Minute' : ' Minuten');
            }
        }

        const next = nextBusinessOpening(civilNow, cfg);
        if (!next) return FALLBACK_TEXT;

        // "ab", nicht "um": die Öffnungszeit ist der frühestmögliche Zeitpunkt,
        // nicht der wahrscheinliche. Laut Tracker wird der über Nacht entstandene
        // Rückstau NICHT zur Öffnung abtelefoniert — die Öffnungsstunde ist die
        // schwächste Stunde des Betriebstags (Sonntag 58 Anrufe um 09:00 gegen
        // 251 um 15:00; Montag 631 um 08:00 gegen 1.007 um 12:00). Eine konkrete
        // Uhrzeit wäre damit ein Versprechen, das fast niemand erlebt.
        const time = formatHHMM(next.open) + ' Uhr';
        if (next.dayOffset === 0) return 'heute ab ' + time;
        if (next.dayOffset === 1) return 'morgen ab ' + time;
        return 'am ' + WEEKDAYS_DE[next.civilDate.isoWeekday - 1] + ' ab ' + time;
    } catch (e) {
        // Bewusst nur warnen: eine kaputte Config darf die Bestätigungsseite nicht zerstören.
        console.warn('callEtaText: Fallback wegen Fehler', e);
        return FALLBACK_TEXT;
    }
}

/**
 * Zerlegt den konfigurierten Text in Abschnitte, damit die Anrufzeit im Template
 * hervorgehoben werden kann.
 *
 * Bewusst KEIN v-html: In diesem Konfigurator überschreibt jeder Query-Parameter
 * per Dot-Notation beliebige Config-Werte (?steps.4.text=...). Mit v-html wäre
 * damit über eine präparierte URL beliebiges HTML und JavaScript auf einer Seite
 * ausführbar, die Kontaktdaten entgegennimmt. Die Abschnitte werden deshalb als
 * getrennte Textknoten gerendert und bleiben escaped.
 *
 * Nicht auflösbare Platzhalter werden entfernt statt angezeigt — es darf nie ein
 * sichtbares {{...}} in der Seite landen.
 *
 * @param {string} template Text aus der Config, z.B. "… ruft Sie {{ANRUFZEIT}} an, …"
 * @param {string} etaText  aufgelöste Anrufzeit
 * @returns {Array<{text: string, bold: boolean}>}
 */
export function buildTextParts(template, etaText) {
    if (typeof template !== 'string' || template === '') return [];

    // Alle Platzhalter außer ANRUFZEIT entfernen, dieser bleibt als Trennmarke.
    let cleaned = template.replace(/\{\{\s*([A-Z_]+)\s*\}\}/g, (match, key) =>
        key === 'ANRUFZEIT' ? match : ''
    );

    // Aufräumen vor dem Zerlegen, damit die Abschnitte danach unangetastet
    // bleiben und Leerzeichen zwischen ihnen erhalten sind.
    cleaned = cleaned.replace(/[ \t]{2,}/g, ' ').replace(/\s+([,.;:!?])/g, '$1').trim();

    const eta = etaText === undefined || etaText === null || etaText === ''
        ? FALLBACK_TEXT
        : String(etaText);

    const chunks = cleaned.split(/\{\{\s*ANRUFZEIT\s*\}\}/);
    const parts = [];

    chunks.forEach((chunk, index) => {
        if (chunk !== '') parts.push({ text: chunk, bold: false });
        // Zwischen zwei Abschnitten stand ein Platzhalter.
        if (index < chunks.length - 1) parts.push({ text: eta, bold: true });
    });

    return parts;
}

export default { callEtaText, buildTextParts, FALLBACK_TEXT };
